'use client';

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { addMonths } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { cn } from '@fshr/shared-react';
import { formatDateRange, formatPrice, simpleLinkRewrite } from '@fshr/shared';
import { rumAddToCartPressed } from '@fshr/shared/rum';
import { useLocale, useTranslations } from '@fshr/context/i18n-context';
import { useTealium } from '@fshr/context/tealium-context';
import BevSafeText from '../../../bev-safe-text';
import Alert from '../../../icon/alert';
import CloseAlt from '../../../icon/close-alt';

import Modal from '../../../modal';
import useProductBookingWidget from './use-product-booking-widget';
import { BOOKING_FLOW_STEP, BOOKING_FORM_ID } from './constants';
import LoadingDots from './loading-dots';
import AvailabilityMessage from './availability-message';
import SelectActiveBooking from './select-active-booking';
import DateSelection from './date-selection';
import PriceOptions from './price-options';
import BookingForm from './booking-form';
import BookingConfirmation from './booking-confirmation';
import BookingSuccess from './booking-success';
import AddedToCartPopup from './added-to-cart-popup';
import RemoveStayConfirmDialog from './remove-stay-confirm-dialog';
import FindReservation from './find-reservation';
import type { DateRange, NormalizedFieldSet, ProductBookingWidgetProps } from './types';
import { ProductBookingWidgetProgressBar } from './product-booking-widget-progress-bar';
import UnavailableBookingWidget from '../../../unavailable-booking-widget';
import { NonBookableWidget } from '../../..//non-bookable-widget';
import LoadingTreeSpinner from './loading-tree-spinner';
import ErrorBoundary from './error-boundary';

/**
 * Rebuilt `AddToCart` widget: same booking logic as `apps/microapps/addToCart`, but the whole flow
 * (price selection → preferences form → confirmation → success) now stays inline inside this widget
 * instead of opening `AddToCartFormModal`. See `use-product-booking-widget.ts` for the ported
 * state/logic and each section file's docblock for what it replaces. Visual structure follows the Figma
 * "Booking Widget" spec (node 25205:37866): a bordered panel with a centred header, collapsed date
 * summary (Step 2), variant selection list, inline preferences form, and a persistent footer CTA.
 */

function LoadingOverlay({ fullScreen = false }: { fullScreen?: boolean }) {
	return (
		<div
			className={cn(
				fullScreen ? 'fixed inset-0 z-50' : 'absolute inset-0 z-10',
				'flex items-center justify-center bg-surface-translucent'
			)}
		>
			<LoadingTreeSpinner />
		</div>
	);
}

function ProductBookingWidget(props: ProductBookingWidgetProps): ReactElement | null {
	const locale = useLocale();
	const { trackInteraction } = useTealium();
	const widget = useProductBookingWidget({ ...props, locale });
	const {
		userStore,
		bookingsStore,
		productAvailabilityStore,
		className,
		property,
		resortName,
		onFindReservationAnalyticsEvent,
		onCancellationPolicyClick,
		onClose,
	} = props;
	const [isChoosingBooking, setIsChoosingBooking] = useState(false);
	const [pendingReservationId, setPendingReservationId] = useState<string | null>(null);
	const [isSubmittingStay, setIsSubmittingStay] = useState(false);
	const [isEditingDates, setIsEditingDates] = useState(
		!widget.hasDatesAndAvailability && !widget.hasActiveBookingDateRange
	);
	const [showVariantError, setShowVariantError] = useState(false);

	const stepThreeRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (widget.selectedPrice) {
			// AC 1.1: align to the start of Step 3 (scroll-margin-top offsets any sticky header so the
			// section header isn't flush against the top edge).
			stepThreeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [widget.selectedPrice?.id]);

	// Ported from `AddToCart.tsx`: today through 13 months out.
	const availableRange = useMemo(() => ({ start: new Date(), end: addMonths(new Date(), 13) }), []);

	const minPrice = useMemo(
		() =>
			widget.prices
				.filter(p => !p.complimentary)
				.reduce<
					(typeof widget.prices)[0] | null
				>((min, p) => (min === null || p.amount < min.amount ? p : min), null),
		[widget.prices]
	);

	if (!widget.shouldShowInteractiveElements) {
		return (
			<NonBookableWidget
				phoneNumber={property.phoneNumberPrimary}
				chatHref={simpleLinkRewrite(`/content/fshr/pages/${locale}/properties/${property.id}/chat`)}
			></NonBookableWidget>
		);
	}

	if (widget.isBooked && !widget.prices) {
		return (
			<UnavailableBookingWidget
				phoneNumber={property.phoneNumberPrimary}
				chatHref={simpleLinkRewrite(`/content/fshr/pages/${locale}/properties/${property.id}/chat`)}
			></UnavailableBookingWidget>
		);
	}

	const dateRangeLabel =
		widget.dateRangeStart && widget.dateRangeEnd
			? formatDateRange(widget.dateRangeStart.toISOString(), widget.dateRangeEnd.toISOString())
			: '';

	let headerTitle = widget.t('Choose Your Dates');
	let bookingStep = 1;
	const totalBookingSteps = 4;
	if (isChoosingBooking) {
		headerTitle = widget.t('Select Your Stay');
		bookingStep = 2;
	} else if (widget.hasActiveBookingDateRange) {
		headerTitle = widget.t('Add to your itinerary');
		// Hotel-guest path: already at step 3 ("Add to Itinerary"); CONFIRMATION bumps to step 4.
		bookingStep = widget.flowStep === BOOKING_FLOW_STEP.CONFIRMATION ? 4 : 3;
	} else if (widget.selectedPrice && widget.flowStep === BOOKING_FLOW_STEP.CONFIRMATION) {
		// Non-hotel-guest path — "Confirm" step (BookingConfirmation screen).
		headerTitle = widget.t('Book Your Experience');
		bookingStep = 4;
	} else if (widget.selectedPrice) {
		// Non-hotel-guest path — Step 3: "Select Preferences" form is open.
		headerTitle = widget.t('Book Your Experience');
		bookingStep = 3;
	} else if (widget.prices.length > 0 && widget.hasDatesAndAvailability) {
		// Non-hotel-guest path — Step 2: price list visible, none selected yet.
		headerTitle = widget.t('Book Your Experience');
		bookingStep = 2;
	}
	const priceFrom = widget.product?.summaries?.price?.from;
	const fullPrice = priceFrom
		? (formatPrice({
				value: priceFrom.amount,
				currencyCode: priceFrom.currencyCode,
				decimalPlaces: 0,
			}) as string)
		: '';
	const callout = fullPrice ? `${widget.t('From {0}', [fullPrice])}` : '';
	const qualifier = priceFrom?.type === 'PER_PERSON' ? widget.t('/person') : '';

	const handleContinueStay = () => {
		if (!pendingReservationId) return;
		setIsSubmittingStay(true);
		const { request } = widget.selectActiveBooking(pendingReservationId);
		request.finally(() => {
			setIsSubmittingStay(false);
			setIsChoosingBooking(false);
		});
	};

	const handleApplyDateRange = (range: DateRange) => {
		widget.applyDateRange(range);
		setIsEditingDates(false);
	};

	const handleChangeDates = () => {
		setIsEditingDates(true);
		widget.closeForm();
		setShowVariantError(false);
	};

	const handleSelectPrice = (priceId: string) => {
		widget.selectPrice(priceId);
		setShowVariantError(false);
		trackInteraction('product detail page', 'booking widget', 'experience variant');
	};

	const handleFooterCtaClick = () => {
		if (!widget.isBooked) {
			// The form submits the selected price's hidden `name` field; with no price picked, use the product's.
			const productName =
				(widget.selectedPrice?.fieldSets as unknown as NormalizedFieldSet[] | undefined)
					?.flatMap(fieldSet => fieldSet.fields ?? [])
					.find(field => field.name === 'name')?.value ?? widget.product?.name;
			rumAddToCartPressed({ cartType: 'product', hotelCode: widget.hotelCode, productName });
		}
		if (!widget.selectedPriceId) {
			setShowVariantError(true);
		}
	};

	const showCalendar =
		!widget.hasActiveBookingDateRange && (isEditingDates || !widget.hasDatesAndAvailability);
	const showDateSummary =
		!widget.hasActiveBookingDateRange && !isEditingDates && widget.hasDatesAndAvailability;
	const showPriceSection =
		!widget.isLoading &&
		widget.prices.length > 0 &&
		widget.hasDatesAndAvailability &&
		!isEditingDates;
	const showFooter = showPriceSection && widget.flowStep === BOOKING_FLOW_STEP.FORM;

	const addMoreHref = widget.product?.landingPageUrl
		? simpleLinkRewrite(widget.product.landingPageUrl)
		: undefined;

	// Ported from `AddToCart.tsx`: an active booking whose product has an availability message takes over
	// the whole widget.
	if (widget.activeBooking && widget.hasDates && widget.product?.availabilityMessage?.message) {
		return (
			<div className="relative flex flex-col gap-300 rounded-[20px] border border-border-subtle bg-surface-default p-300">
				<AvailabilityMessage
					title={widget.t(widget.product.availabilityMessage.title)}
					subTitle={widget.t(widget.product.availabilityMessage.message)}
				/>
			</div>
		);
	}

	// Ported from `AddToCart.tsx`: with no active booking selected but multiple candidate bookings,
	// prompt the user to pick one instead of showing the booking form.
	if (!widget.activeBooking && widget.hasMultipleBookings) {
		return (
			<div
				className={cn(
					'relative flex flex-col rounded-[20px] border border-border-subtle bg-surface-default lg:h-[640px] lg:w-full lg:max-w-[507px] lg:overflow-hidden',
					className
				)}
				data-testid="product-booking-widget"
			>
				<div className="flex flex-col items-center gap-150 border-b border-border-subtle px-300 pb-200 pt-300 text-center lg:shrink-0">
					<span className="text-subtitle1 text-text-heading">
						<BevSafeText>{widget.t('Select Your Stay')}</BevSafeText>
					</span>
					<ProductBookingWidgetProgressBar step={2} totalSteps={totalBookingSteps} />
				</div>
				<div className="px-300 py-300 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
					<SelectActiveBooking
						bookings={widget.bookingsForProperty}
						resortName={resortName}
						selectedReservationId={pendingReservationId}
						onSelectionChange={setPendingReservationId}
						t={widget.t}
					/>
				</div>
				<div className="flex justify-end border-t border-border-subtle px-300 py-200 lg:shrink-0">
					<button
						type="button"
						disabled={!pendingReservationId || isSubmittingStay}
						onClick={handleContinueStay}
						className="text-button1 rounded-sm bg-button-primary px-300 py-150 text-button-primary-inverse hover:bg-button-primary-hover disabled:bg-button-primary-disabled disabled:text-button-primary-disabled-inverse"
					>
						{widget.t('Continue')}
					</button>
				</div>
			</div>
		);
	}

	if (isChoosingBooking) {
		return (
			<div
				data-booking-widget
				className={cn(
					'relative flex flex-col rounded-[20px] border border-border-subtle bg-surface-default lg:h-[640px] lg:w-full lg:max-w-[507px] lg:overflow-hidden',
					className
				)}
				data-testid="product-booking-widget"
			>
				<div className="flex flex-col items-center gap-150 border-b border-border-subtle px-300 pb-200 pt-300 text-center lg:shrink-0">
					<span className="text-subtitle1 text-text-heading">
						<BevSafeText>{headerTitle}</BevSafeText>
					</span>
					<ProductBookingWidgetProgressBar step={bookingStep} totalSteps={totalBookingSteps} />
				</div>
				<div className="px-300 py-300 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
					<SelectActiveBooking
						bookings={widget.bookingsForProperty}
						resortName={resortName}
						selectedReservationId={pendingReservationId}
						onSelectionChange={setPendingReservationId}
						t={widget.t}
					/>
				</div>
				<div className="flex justify-end border-t border-border-subtle px-300 py-200 lg:shrink-0">
					<button
						type="button"
						disabled={!pendingReservationId || isSubmittingStay}
						onClick={handleContinueStay}
						className="text-button1 rounded-sm bg-button-primary px-300 py-150 text-button-primary-inverse hover:bg-button-primary-hover disabled:bg-button-primary-disabled disabled:text-button-primary-disabled-inverse"
					>
						{widget.t('Continue')}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div
			data-booking-widget
			className={cn(
				'relative flex flex-col rounded-[20px] border border-border-subtle bg-surface-default lg:h-[640px] lg:w-full lg:max-w-[507px] lg:overflow-hidden',
				className
			)}
			data-testid="product-booking-widget"
		>
			<div className="relative flex flex-col items-center bg-surface-default px-300 py-200 text-center lg:shrink-0">
				{onClose && (
					<button
						type="button"
						onClick={onClose}
						aria-label={widget.t('Close')}
						className="absolute right-0 top-0 p-020 text-text-primary"
					>
						<CloseAlt width={20} height={20} />
					</button>
				)}
				<span className="text-subtitle1 text-text-heading !text-[20px]">
					<BevSafeText>{headerTitle}</BevSafeText>
				</span>
			</div>
			<ProductBookingWidgetProgressBar step={bookingStep} totalSteps={totalBookingSteps} />

			<div
				className="flex flex-col gap-300 lg:min-h-0 lg:flex-1 lg:overflow-y-auto"
				data-testid="product-booking-widget-body"
			>
				{widget.hasActiveBookingDateRange && (
					<div className="flex flex-col gap-100 px-300 py-300 ">
						<span className="text-label text-text-primary">
							{widget.t('Availability For Your Stay')}
						</span>
						<div className="flex items-center justify-between gap-200">
							<span className="text-subtitle2 text-text-primary">
								<BevSafeText>{dateRangeLabel}</BevSafeText>
							</span>
							{widget.hasMultipleBookings && (
								<button
									type="button"
									onClick={() => {
										setPendingReservationId(null);
										setIsChoosingBooking(true);
									}}
									className="text-body3 text-text-primary underline"
								>
									{widget.t('Change')}
								</button>
							)}
						</div>
					</div>
				)}

				{showCalendar && (
					<DateSelection
						locale={widget.locale}
						strictRangeSelect={true}
						selectedRange={{ start: widget.dateRangeStart, end: widget.dateRangeEnd }}
						availableRange={availableRange}
						availability={widget.product?.calendar?.availability}
						callout={callout}
						qualifier={qualifier}
						cancellationPolicy={widget.product?.terms}
						t={widget.t}
						onApply={handleApplyDateRange}
						allowPartialRange={true}
					/>
				)}

				{showDateSummary && (
					<div className="flex flex-col gap-100 px-300 py-300" data-testid="date-summary">
						<div className="flex items-start justify-between gap-200">
							<span className="text-caption2 text-text-secondary">
								{widget.t('Check Availability')}
							</span>
							<button
								type="button"
								onClick={handleChangeDates}
								className="text-body3 shrink-0 text-text-primary underline"
							>
								{widget.t('Change')}
							</button>
						</div>
						<span className="text-subtitle1 text-text-heading">{dateRangeLabel}</span>
					</div>
				)}

				{showVariantError && (
					<div
						role="alert"
						className="flex items-start gap-100 px-300 py-300 rounded-sm bg-error-container-subtle p-150"
					>
						<span className="mt-020 shrink-0">
							<Alert width={16} height={16} fill="var(--color-error-icon)" />
						</span>
						<p className="text-body3 text-error-text">
							{widget.t('Please select an experience to continue')}
						</p>
					</div>
				)}

				{widget.isLoading && <LoadingDots />}

				{showPriceSection && (
					<div className="flex flex-col gap-300 px-300 py-300 border-t border-border-subtle pt-300">
						<div className="flex items-center justify-between gap-200">
							<span className="text-subtitle2 text-text-heading">
								{widget.t('Select Experience')}
							</span>
							{widget.selectedPrice && (
								<button
									type="button"
									onClick={() => {
										widget.closeForm();
										setShowVariantError(false);
									}}
									className="text-body3 shrink-0 text-text-primary underline"
								>
									{widget.t('Change')}
								</button>
							)}
						</div>

						<PriceOptions
							prices={widget.prices}
							locale={widget.locale}
							buttonLabel={widget.buttonLabel}
							selectedPriceId={widget.selectedPriceId}
							t={widget.t}
							onSelect={handleSelectPrice}
						/>

						{widget.selectedPrice && widget.flowStep !== BOOKING_FLOW_STEP.SUCCESS && (
							<div
								ref={stepThreeRef}
								className="flex flex-col gap-200 border-t border-border-subtle pt-300"
							>
								{/* AC 4.1: "Select Preferences" section header */}
								<span className="text-subtitle2 text-text-heading">
									<BevSafeText>{widget.t('Select Preferences')}</BevSafeText>
								</span>

								{widget.flowStep === BOOKING_FLOW_STEP.FORM && (
									<>
										<BookingForm
											price={widget.selectedPrice}
											isBooked={widget.isBooked}
											isSubmitting={widget.isSubmitting}
											t={widget.t}
											onSubmit={formData => widget.submitBookingForm(formData)}
											formId={BOOKING_FORM_ID}
											hideSubmitButton
										/>
										{Boolean(widget.error) && (
											<div
												role="alert"
												className="flex items-start gap-100 rounded-sm bg-error-container-subtle p-150"
											>
												<span className="mt-020 shrink-0">
													<Alert width={16} height={16} fill="var(--color-error-icon)" />
												</span>
												<p className="text-body3 text-error-text">
													{(widget.error as { data?: { title?: string } })?.data?.title ?? (
														<>
															{widget.t('Something went wrong. Please try again or')}{' '}
															<a
																href={simpleLinkRewrite(
																	`/content/fshr/pages/${locale}/properties/${property.id}/contact`
																)}
																className="underline"
															>
																{widget.t('contact the property')}
															</a>
															{'.'}
														</>
													)}
												</p>
											</div>
										)}
									</>
								)}

								{widget.flowStep === BOOKING_FLOW_STEP.CONFIRMATION && widget.addedEventProduct && (
									<>
										<BookingConfirmation
											locale={widget.locale}
											eventProduct={widget.addedEventProduct}
											isSubmitting={widget.isSubmitting}
											t={widget.t}
											onGoBack={widget.goBackToForm}
											onConfirm={widget.confirmOnItinerary}
										/>
										{Boolean(widget.error) && (
											<div
												role="alert"
												className="flex items-start gap-100 rounded-sm bg-error-container-subtle p-150"
											>
												<span className="mt-020 shrink-0">
													<Alert width={16} height={16} fill="var(--color-error-icon)" />
												</span>
												<p className="text-body3 text-error-text">
													{(widget.error as { data?: { title?: string } })?.data?.title ?? (
														<>
															{widget.t('Something went wrong. Please try again or')}{' '}
															<a
																href={simpleLinkRewrite(
																	`/content/fshr/pages/${locale}/properties/${property.id}/contact`
																)}
																className="underline"
															>
																{widget.t('contact the property')}
															</a>
															{'.'}
														</>
													)}
												</p>
											</div>
										)}
									</>
								)}
							</div>
						)}
					</div>
				)}
			</div>

			{widget.flowStep === BOOKING_FLOW_STEP.SUCCESS && (
				<Modal close={widget.closeForm} mobileFullScreen={false} mobileAnimate>
					<BookingSuccess
						product={widget.product}
						itineraryDisclaimer={widget.itineraryDisclaimer}
						itineraryPath={widget.itineraryPath}
						addMoreHref={addMoreHref}
						t={widget.t}
						onAddMore={widget.closeForm}
						onViewItinerary={widget.viewInItinerary}
					/>
				</Modal>
			)}

			{widget.addedToCartItem && (
				<AddedToCartPopup
					item={widget.addedToCartItem}
					locale={widget.locale}
					t={widget.t}
					onClose={widget.closeAddedToCartPopup}
					onViewCart={widget.viewCartFromPopup}
				/>
			)}

			{!widget.isLoading && widget.product?.availabilityMessage && (
				<AvailabilityMessage
					title={
						widget.product.availabilityMessage.title &&
						widget.t(widget.product.availabilityMessage.title)
					}
					subTitle={
						widget.product.availabilityMessage.message &&
						widget.t(widget.product.availabilityMessage.message)
					}
				/>
			)}

			{widget.shouldShowFindReservationComponent && (
				<FindReservation
					t={widget.t}
					property={{
						name: property.officialName,
						owsCode: property.bookingConfig.owsCode,
					}}
					onSubmit={widget.findReservation}
					onAnalyticsEvent={onFindReservationAnalyticsEvent}
				/>
			)}

			<RemoveStayConfirmDialog
				isOpen={widget.isRemoveStayConfirmOpen}
				t={widget.t}
				onConfirm={widget.confirmRemoveStayAndRetry}
				onCancel={widget.cancelRemoveStayConfirm}
			/>

			{/* Figma "Booking Widget Footer" (Frame 14676) itemSpacing between
			    the price column and CTA button is 20px, not the flat 30px this
			    had; its own padding (30/20/20) was already an exact match. */}
			{showFooter && (
				<div className="sticky bottom-0 flex items-end justify-between gap-200 border-t border-border-subtle bg-surface-default px-300 pb-200 pt-200 lg:shrink-0">
					<div className="flex min-w-0 flex-col gap-060">
						{minPrice && (
							<div className="flex flex-wrap items-baseline gap-x-inline-2xs">
								<span className="text-body3 text-text-secondary">{widget.t('From')} </span>
								<span className="text-subtitle2 text-text-heading">
									{formatPrice({
										value: minPrice.amount,
										currencyCode: minPrice.currencyCode,
										locale: widget.locale,
										decimalPlaces: 0,
									})}
								</span>
								{minPrice.type === 'PER_PERSON' && (
									<span className="text-body3 text-text-secondary">{widget.t('/person')}</span>
								)}
							</div>
						)}
						{onCancellationPolicyClick && (
							<button
								type="button"
								onClick={onCancellationPolicyClick}
								className="text-body3 self-start text-text-primary underline"
							>
								{widget.t('Cancellation Policy')}
							</button>
						)}
					</div>

					<button
						type={widget.selectedPriceId ? 'submit' : 'button'}
						form={widget.selectedPriceId ? BOOKING_FORM_ID : undefined}
						onClick={handleFooterCtaClick}
						disabled={widget.isSubmitting}
						data-testid="booking-footer-cta"
						className="text-button1 shrink-0 rounded-sm bg-button-primary px-300 py-150 text-button-primary-inverse hover:bg-button-primary-hover disabled:bg-button-primary-disabled disabled:text-button-primary-disabled-inverse"
					>
						{widget.buttonLabel}
					</button>
				</div>
			)}

			{(userStore.fetching || bookingsStore.fetching) && <LoadingOverlay fullScreen />}

			{productAvailabilityStore.fetching && <LoadingOverlay />}
		</div>
	);
}

const ObservedProductBookingWidget = observer(ProductBookingWidget);

/** Self-contained fallback shown when the booking flow throws - keeps the panel's footprint so the
 * surrounding PDP layout doesn't jump when it swaps in for the widget. */
function BookingWidgetErrorFallback({
	t,
}: {
	t: ReturnType<typeof useTranslations>;
}): ReactElement {
	return (
		<div
			data-testid="product-booking-widget-error"
			className={cn(
				'relative flex flex-col items-center gap-150 rounded-[20px] border border-border-subtle',
				'bg-surface-default px-300 py-400 text-center'
			)}
		>
			<Alert width={20} height={20} fill="var(--color-error-icon)" />
			<p className="text-body3 text-error-text">
				{t(
					'Something went wrong loading the booking widget. Please refresh the page or contact the property to book.'
				)}
			</p>
		</div>
	);
}

/**
 * Default export: the widget wrapped in an `ErrorBoundary`. An unexpected throw anywhere in the
 * booking flow (hook, render, a child section) now renders `BookingWidgetErrorFallback` in place of
 * the widget instead of propagating up and unmounting the entire `PdpTemplate` tree - i.e. the
 * Product Detail / Signature Product Detail page stays up with every other section intact.
 */
function ProductBookingWidgetWithBoundary(props: ProductBookingWidgetProps): ReactElement {
	const t = useTranslations();
	return (
		<ErrorBoundary fallback={<BookingWidgetErrorFallback t={t} />}>
			<ObservedProductBookingWidget {...props} />
		</ErrorBoundary>
	);
}

export default ProductBookingWidgetWithBoundary;
